<template>
  <section>
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field grouped group-multiline>
            <b-field>
              <b-input
                v-model="search"
                placeholder="Search..."
                type="search"
                icon="magnify"
                @input="onSearching"
              ></b-input>
              <p class="control">
                <button class="button is-primary" @click="enterSearching">
                  {{ $t('attributes.search') }}
                </button>
              </p>
            </b-field>
          </b-field>
        </div>
      </div>
      <div class="level-right"></div>
    </nav>

    <b-table
      :data="items"
      :loading="loading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      paginated
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        :visible="false"
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="username"
        :label="$t('attributes.account')"
        width="160"
        sortable
        v-slot="props"
      >
        {{ props.row.username }}
      </b-table-column>

      <b-table-column
        field="display_name"
        :label="$t('attributes.user_name')"
        width="160"
        sortable
        v-slot="props"
      >
        {{ props.row.display_name }}
      </b-table-column>

      <b-table-column
        field="email"
        :label="$t('attributes.email')"
        width="240"
        sortable
        v-slot="props"
      >
        {{ props.row.email }}
      </b-table-column>

      <b-table-column
        field="identity"
        :label="$t('attributes.identity_number')"
        width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.identity }}
      </b-table-column>

      <b-table-column
        field="phone_number"
        :label="$t('attributes.tel')"
        width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.phone_number }}
      </b-table-column>

      <b-table-column
        field="user_activities"
        :label="$t('attributes.activities_involved')"
        v-slot="props"
      >
        <p v-for="(activity, idx) in props.row.user_activities" :key="idx">
          {{ activity.activity_name }}
        </p>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.function')"
        :width="160"
        centered
        v-slot="props"
      >
        <div class="buttons">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
          <b-button
            type="is-danger"
            :disabled="props.row.user_activities.length > 0"
            @click.prevent="onDelete(props.row)"
          >
            {{ $t('attributes.remove') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p>{{ $t('attributes.empty') }}</p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <activity-user-form
        :activity="collaborate"
        v-bind="selectedUser"
        :sending="sending"
        @onSend="onSend"
      />
    </b-modal>

    <b-loading :active.sync="isProcessing" :is-full-page="true"></b-loading>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { FIRM_COLLABORATES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import ActivityUserForm from './ActivityUserForm'

export default {
  name: 'CollaborateUsers',
  components: { ActivityUserForm },

  props: {
    collaborate: {
      required: true,
      type: Object
    }
  },
  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      filterStatus: '',
      search: '',
      sortField: 'username',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',
      page: 1,
      perPage: 10,

      isModalActive: false,
      selectedUser: null,
      sending: false
    }
  },
  watch: {
    collaborate(value) {
      this.loadUsers(value.id)
    }
  },
  computed: {
    isProcessing() {
      return this.loading || this.sending
    }
  },
  mounted() {
    this.loadUsers(this.collaborate.id)
  },
  methods: {
    async loadUsers(collaborateId) {
      try {
        const api = `${FIRM_COLLABORATES_API}/${collaborateId}/users`
        const params = {
          search: this.search,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loading = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items.map(user => {
          const user_activities = user.user_activities.filter(a => a.active)

          return {
            ...user,
            user_activities
          }
        })
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('user', e)
      } finally {
        this.loading = false
      }
    },

    onSearching: debounce(function() {
      this.loadUsers(this.collaborate.id)
    }, 500),
    enterSearching() {
      this.loadUsers(this.collaborate.id)
    },
    onPageChange(page) {
      this.page = page
      this.loadUsers(this.collaborate.id)
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadUsers(this.collaborate.id)
    },

    onAdd() {
      this.isEditing = false
      this.selectedUser = {}
      this.isModalActive = true
    },
    onModify(selected) {
      this.isEditing = true
      this.selectedUser = selected
      this.isModalActive = true
    },
    onDelete(selected) {
      this.$buefy.dialog.confirm({
        title: `${this.$t('attributes.remove')} ${this.$t('attributes.user')}`,
        message: `${this.$t('attributes.confirm_remove')} ${
          selected.username
        } ${selected.display_name}?`,
        cancelText: this.$t('attributes.cancel'),
        confirmText: this.$t('attributes.confirm'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.deleteUser(selected)
        }
      })
    },
    onSend(item) {
      if (!this.isEditing) {
        this.createUser(item)
      } else {
        this.updateUser(item)
      }
    },
    async createUser(item) {
      try {
        const api = `${FIRM_COLLABORATES_API}/${this.collaborate.id}/users`

        this.sending = true
        await this.axios.post(api, item)
        this.isModalActive = false

        notifyService.success(this.$t('attributes.add_succeed'))
        await this.loadUsers(this.collaborate.id)
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateUser(item) {
      try {
        const api = `${FIRM_COLLABORATES_API}/${this.collaborate.id}/users/${item.id}`

        this.sending = true
        await this.axios.put(api, item)
        this.isModalActive = false

        notifyService.success(this.$t('attributes.update_succeed'))
        await this.loadUsers(this.collaborate.id)
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async deleteUser(item) {
      try {
        const api = `${FIRM_COLLABORATES_API}/${this.collaborate.id}/users/${item.id}`

        this.sending = true
        await this.axios.delete(api)

        notifyService.success(this.$t('attributes.remove_succeed'))
        await this.loadUsers(this.collaborate.id)
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>
